import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import "@/App.css";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertTriangle, Trash2, RefreshCw, Loader2, CheckCircle2, LogOut, Settings, CreditCard, HelpCircle, ExternalLink, Download, Shield, Rocket, FileSpreadsheet, LifeBuoy, Send, Edit3 } from "lucide-react";
import AdminPanel from "@/components/AdminPanel";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const PAYPAL_CLIENT_ID = "BAANaLuBXgvojcqw5FwV1zevja-4ib3O5QeipBkNz6rAFz3C-GNXIsw1V7Dmg0BO6SQrT0_vqGwUGcdxRM";

// Global Axios interceptor: auto-logout on 401 (expired/invalid token)
axios.interceptors.response.use(
  response => response,
  error => {
    if (error.response?.status === 401 && localStorage.getItem("token")) {
      localStorage.removeItem("token");
      window.location.reload();
    }
    return Promise.reject(error);
  }
);

function App() {
  // Auth state
  const [user, setUser] = useState(null);
  const [authMode, setAuthMode] = useState("register");
  const [authEmail, setAuthEmail] = useState("");
  const [authPassword, setAuthPassword] = useState("");
  const [authLoading, setAuthLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [showAdmin, setShowAdmin] = useState(false);
  const [showAuthPrompt, setShowAuthPrompt] = useState(false);

  // Tab state
  const [activeTab, setActiveTab] = useState("retirement");

  // Trial / Guest state
  const [trialCreds, setTrialCreds] = useState({ client_id: "", client_secret: "" });
  const [trialValidated, setTrialValidated] = useState(false);
  const [trialUsed, setTrialUsed] = useState(() => localStorage.getItem("trial_used") === "true");
  const [validatingCreds, setValidatingCreds] = useState(false);

  // Settings state
  const [showSettings, setShowSettings] = useState(false);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [walmartClientId, setWalmartClientId] = useState("");
  const [walmartSecret, setWalmartSecret] = useState("");
  const [savingCreds, setSavingCreds] = useState(false);
  const [pricingPlans, setPricingPlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null);

  // Ticket state
  const [showTicketDialog, setShowTicketDialog] = useState(false);
  const [ticketSubject, setTicketSubject] = useState("");
  const [ticketMessage, setTicketMessage] = useState("");
  const [ticketEmail, setTicketEmail] = useState("");
  const [submittingTicket, setSubmittingTicket] = useState(false);

  // Retirement tab inventory state
  const [inventory, setInventory] = useState([]);
  const [selectedSkus, setSelectedSkus] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [loadAmount, setLoadAmount] = useState(100);
  const [statusFilter, setStatusFilter] = useState("all");
  const [deletionJob, setDeletionJob] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deletionStats, setDeletionStats] = useState({ total_unique_retired: 0 });
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  // CSV Exporter tab state
  const [csvInventory, setCsvInventory] = useState([]);
  const [csvLoading, setCsvLoading] = useState(false);
  const [csvLoadAmount, setCsvLoadAmount] = useState(1000);
  const [csvStatusFilter, setCsvStatusFilter] = useState("all");

  const pollIntervalRef = useRef(null);
  const googleAuthProcessed = useRef(false);

  const isGuest = !user;
  const hasCredentials = isGuest ? trialValidated : user?.has_walmart_credentials;
  const FREE_TIER_LIMIT = 5;
  const FREE_CSV_LIMIT = 5;

  const handleGoogleLogin = () => {
    const redirectUrl = window.location.origin + '/';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  useEffect(() => {
    const hash = window.location.hash;
    if (hash && hash.includes('session_id=') && !googleAuthProcessed.current) {
      googleAuthProcessed.current = true;
      const sessionId = hash.split('session_id=')[1]?.split('&')[0];
      if (sessionId) processGoogleAuth(sessionId);
    }
  }, []);

  const processGoogleAuth = async (sessionId) => {
    setGoogleLoading(true);
    try {
      const response = await axios.post(`${API}/auth/google`, { session_id: sessionId });
      localStorage.setItem("token", response.data.token);
      setUser(response.data.user);
      toast.success("Signed in with Google!");
      window.history.replaceState(null, '', window.location.pathname);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Google sign-in failed");
      window.history.replaceState(null, '', window.location.pathname);
    } finally { setGoogleLoading(false); }
  };

  useEffect(() => {
    if (window.location.hash?.includes('session_id=')) return;
    const token = localStorage.getItem("token");
    if (token) {
      // Check if token is expired before fetching
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        if (payload.exp && payload.exp * 1000 < Date.now()) {
          localStorage.removeItem("token");
          setUser(null);
          return;
        }
      } catch { localStorage.removeItem("token"); return; }
      fetchUser(token);
    }
    // Hide platform watermark
    const hideWatermark = () => {
      const badge = document.getElementById('emergent-badge');
      if (badge) badge.style.setProperty('display', 'none', 'important');
    };
    const wmInterval = setInterval(hideWatermark, 100);
    // Periodically check token expiry (every 60s)
    const tokenCheck = setInterval(() => {
      const t = localStorage.getItem("token");
      if (t) {
        try {
          const p = JSON.parse(atob(t.split('.')[1]));
          if (p.exp && p.exp * 1000 < Date.now()) {
            localStorage.removeItem("token");
            setUser(null);
            toast.info("Session expired. Please sign in again.");
          }
        } catch { /* invalid token */ }
      }
    }, 60000);
    return () => { clearInterval(wmInterval); clearInterval(tokenCheck); if (pollIntervalRef.current) clearInterval(pollIntervalRef.current); };
  }, []);

  useEffect(() => { if (user) { fetchStats(); trackVisit(); } }, [user]);

  const getAuthHeader = () => {
    const token = localStorage.getItem("token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const trackVisit = async () => {
    try { await axios.post(`${API}/track/visit`, { path: window.location.pathname }, { headers: getAuthHeader() }); } catch {}
  };

  const fetchUser = async (token) => {
    try {
      const response = await axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } });
      setUser(response.data);
    } catch { localStorage.removeItem("token"); }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/walmart/stats`, { headers: getAuthHeader() });
      setDeletionStats(response.data);
    } catch {}
  };

  const handleAuth = async (e) => {
    e.preventDefault();
    setAuthLoading(true);
    try {
      const endpoint = authMode === "login" ? "/auth/login" : "/auth/register";
      const response = await axios.post(`${API}${endpoint}`, { email: authEmail, password: authPassword });
      localStorage.setItem("token", response.data.token);
      setUser(response.data.user);
      setShowAuthPrompt(false);
      toast.success(authMode === "login" ? "Welcome back!" : "Account created!");
      setAuthEmail(""); setAuthPassword("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Authentication failed");
    } finally { setAuthLoading(false); }
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    setUser(null); setInventory([]); setSelectedSkus(new Set()); setCsvInventory([]); setShowAdmin(false);
  };

  const openUpgradeDialog = async () => {
    setShowUpgrade(true);
    if (pricingPlans.length === 0) {
      try {
        const res = await axios.get(`${API}/payments/plans`);
        setPricingPlans(res.data.plans);
        if (!selectedPlan && res.data.plans.length > 0) setSelectedPlan(res.data.plans[2]?.plan_id || res.data.plans[0]?.plan_id);
      } catch { /* use empty fallback */ }
    }
  };

  const submitTicket = async () => {
    if (!ticketSubject || ticketSubject.length < 3) { toast.error("Please enter a subject (at least 3 characters)"); return; }
    if (!ticketMessage || ticketMessage.length < 10) { toast.error("Please describe your issue (at least 10 characters)"); return; }
    setSubmittingTicket(true);
    try {
      const hdrs = isGuest ? {} : getAuthHeader();
      const res = await axios.post(`${API}/tickets`, { subject: ticketSubject, message: ticketMessage, email: ticketEmail }, { headers: hdrs });
      toast.success(`Ticket #${res.data.ticket_id} submitted! We'll get back to you soon.`);
      setShowTicketDialog(false);
      setTicketSubject(""); setTicketMessage(""); setTicketEmail("");
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to submit ticket"); }
    finally { setSubmittingTicket(false); }
  };

  const validateTrialCredentials = async () => {
    if (!trialCreds.client_id || !trialCreds.client_secret) { toast.error("Please enter both Client ID and Secret"); return; }
    setValidatingCreds(true);
    try {
      await axios.post(`${API}/trial/validate-credentials`, trialCreds);
      setTrialValidated(true);
      toast.success("Credentials validated! You can now load your inventory.");
    } catch (err) { toast.error(err.response?.data?.detail || "Invalid credentials"); }
    finally { setValidatingCreds(false); }
  };

  const saveWalmartCredentials = async () => {
    if (!walmartClientId || !walmartSecret) { toast.error("Please enter both Client ID and Secret"); return; }
    setSavingCreds(true);
    try {
      await axios.post(`${API}/walmart/credentials`, { client_id: walmartClientId, client_secret: walmartSecret }, { headers: getAuthHeader() });
      toast.success("Walmart credentials saved!");
      setUser({ ...user, has_walmart_credentials: true });
      setShowSettings(false); setWalmartClientId(""); setWalmartSecret("");
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to save credentials"); }
    finally { setSavingCreds(false); }
  };

  // === RETIREMENT TAB: Load Inventory ===
  const loadInventory = async () => {
    if (!hasCredentials) { toast.error("Please validate your Walmart credentials first"); return; }
    setLoading(true); setInventory([]); setSelectedSkus(new Set());
    try {
      let response;
      if (isGuest) {
        response = await axios.post(`${API}/trial/inventory?max_items=${loadAmount}&lifecycle_status=ACTIVE`, trialCreds, { timeout: 180000 });
      } else {
        response = await axios.get(`${API}/walmart/inventory?max_items=${loadAmount}&lifecycle_status=ACTIVE`, { headers: getAuthHeader(), timeout: 180000 });
      }
      const items = response.data.items;
      setInventory(items);
      toast.success(`Loaded ${items.length} active items`);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to load inventory"); }
    finally { setLoading(false); }
  };

  // === CSV TAB: Load Inventory ===
  const loadCsvInventory = async () => {
    if (!hasCredentials) { toast.error("Please validate your Walmart credentials first"); return; }
    setCsvLoading(true); setCsvInventory([]);
    try {
      let response;
      if (isGuest) {
        response = await axios.post(`${API}/trial/inventory?max_items=${csvLoadAmount}&lifecycle_status=ACTIVE`, trialCreds, { timeout: 180000 });
      } else {
        response = await axios.get(`${API}/walmart/inventory?max_items=${csvLoadAmount}&lifecycle_status=ACTIVE`, { headers: getAuthHeader(), timeout: 180000 });
      }
      setCsvInventory(response.data.items);
      toast.success(`Loaded ${response.data.items.length} active items for export`);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to load inventory"); }
    finally { setCsvLoading(false); }
  };

  const exportCSV = (items) => {
    if (!items || items.length === 0) { toast.error("No inventory loaded to export"); return; }
    // Check free tier CSV limit
    if (isGuest && trialUsed) {
      toast.error("Trial used. Create an account and upgrade for unlimited CSV exports.");
      setShowAuthPrompt(true); return;
    }
    if (isGuest && items.length > FREE_CSV_LIMIT) {
      toast.error(`Free trial CSV export limited to ${FREE_CSV_LIMIT} items. Create an account for more.`);
      setShowAuthPrompt(true); return;
    }
    if (!isGuest && user.plan === "free" && user.has_used_free_tier && items.length > FREE_CSV_LIMIT) {
      toast.error(`Free tier CSV export limited to ${FREE_CSV_LIMIT} items. Upgrade for unlimited exports.`);
      openUpgradeDialog(); return;
    }
    const csvHeaders = ["SKU", "Product Name", "Product ID", "WPID", "UPC", "GTIN", "Product Type", "Price", "Currency", "Quantity", "Lifecycle Status", "Published Status", "Shelf", "Unpublished Reasons"];
    const rows = items.map(item => [
      item.sku,
      `"${(item.product_name || "").replace(/"/g, '""')}"`,
      item.product_id || "",
      item.wpid || "",
      item.upc || "",
      item.gtin || "",
      `"${(item.product_type || "").replace(/"/g, '""')}"`,
      item.price ?? "",
      item.currency || "USD",
      item.quantity ?? "",
      item.lifecycle_status || "",
      item.published_status || "",
      `"${(item.shelf || "").replace(/"/g, '""')}"`,
      `"${(item.unpublished_reasons || "").replace(/"/g, '""')}"`
    ]);
    const csv = [csvHeaders.join(","), ...rows.map(r => r.join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url;
    a.download = `walmart_inventory_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click(); URL.revokeObjectURL(url);
    toast.success(`Exported ${items.length} items to CSV`);
    // Mark trial used for guest CSV exports
    if (isGuest && !trialUsed) {
      localStorage.setItem("trial_used", "true"); setTrialUsed(true);
      setTimeout(() => setShowAuthPrompt(true), 1500);
    }
  };

  const isFreeTier = isGuest || (user && user.plan === "free");
  const freeItemLimit = FREE_TIER_LIMIT;

  const toggleSelectAll = () => {
    const filtered = filteredInventory;
    if (selectedSkus.size === filtered.length && filtered.length > 0) setSelectedSkus(new Set());
    else {
      const selectable = isFreeTier ? filtered.slice(0, freeItemLimit) : filtered;
      setSelectedSkus(new Set(selectable.map(item => item.sku)));
    }
  };

  const filteredInventory = statusFilter === "all"
    ? inventory : inventory.filter(item => item.lifecycle_status === statusFilter || item.published_status === statusFilter);

  const filteredCsvInventory = csvStatusFilter === "all"
    ? csvInventory : csvInventory.filter(item => item.lifecycle_status === csvStatusFilter || item.published_status === csvStatusFilter);

  const toggleSelect = (sku) => {
    if (isFreeTier) {
      const idx = filteredInventory.findIndex(i => i.sku === sku);
      if (idx >= freeItemLimit) {
        openUpgradeDialog();
        return;
      }
    }
    const newSelected = new Set(selectedSkus);
    if (newSelected.has(sku)) newSelected.delete(sku); else newSelected.add(sku);
    setSelectedSkus(newSelected);
  };

  const startDeletion = async () => {
    const skusToDelete = Array.from(selectedSkus);
    if (skusToDelete.length === 0) { toast.error("No items selected"); return; }
    if (isGuest) {
      if (trialUsed) { toast.error("Trial already used. Create an account and upgrade to continue."); setShowAuthPrompt(true); return; }
      if (skusToDelete.length > FREE_TIER_LIMIT) { toast.error(`Trial limited to ${FREE_TIER_LIMIT} items.`); setShowAuthPrompt(true); return; }
    }
    if (!isGuest && user.plan === "free") {
      if (user.has_used_free_tier) { toast.error("Free tier exhausted. Please upgrade."); openUpgradeDialog(); return; }
      if (skusToDelete.length > FREE_TIER_LIMIT) { toast.error(`Free tier limited to ${FREE_TIER_LIMIT} items.`); openUpgradeDialog(); return; }
    }
    setIsDeleting(true); setShowDeleteDialog(false);
    try {
      const skuNames = {};
      inventory.forEach(item => { if (selectedSkus.has(item.sku)) skuNames[item.sku] = item.product_name; });
      let response;
      if (isGuest) {
        response = await axios.post(`${API}/trial/delete`, { client_id: trialCreds.client_id, client_secret: trialCreds.client_secret, skus: skusToDelete, sku_names: skuNames });
      } else {
        response = await axios.post(`${API}/walmart/delete`, { skus: skusToDelete, sku_names: skuNames }, { headers: getAuthHeader() });
      }
      const jobId = response.data.job_id;
      pollIntervalRef.current = setInterval(async () => {
        try {
          const statusResponse = await axios.get(`${API}/walmart/delete/${jobId}`, { headers: isGuest ? {} : getAuthHeader() });
          setDeletionJob(statusResponse.data);
          if (statusResponse.data.deleted_skus?.length > 0) {
            setInventory(prev => prev.filter(item => !statusResponse.data.deleted_skus.includes(item.sku)));
            setSelectedSkus(prev => { const s = new Set(prev); statusResponse.data.deleted_skus.forEach(sku => s.delete(sku)); return s; });
          }
          if (statusResponse.data.status === "completed" || statusResponse.data.status === "failed") {
            clearInterval(pollIntervalRef.current);
            setIsDeleting(false);
            if (!isGuest) { fetchStats(); fetchUser(localStorage.getItem("token")); }
            if (statusResponse.data.status === "completed") {
              toast.success(`Deleted ${statusResponse.data.deleted_count} items!`);
              if (isGuest && statusResponse.data.deleted_count > 0) {
                localStorage.setItem("trial_used", "true"); setTrialUsed(true);
                setTimeout(() => setShowAuthPrompt(true), 5000);
              }
              if (!isGuest && user.plan === "free" && statusResponse.data.deleted_count > 0) {
                setTimeout(() => openUpgradeDialog(), 5000);
              }
            }
          }
        } catch (err) {
          if (err.response?.status === 404) { clearInterval(pollIntervalRef.current); setIsDeleting(false); setDeletionJob(null); }
        }
      }, 500);
    } catch (err) {
      setIsDeleting(false);
      if (err.response?.status === 403) {
        toast.error(err.response.data.detail);
        if (isGuest) { setShowAuthPrompt(true); localStorage.setItem("trial_used", "true"); setTrialUsed(true); }
        else openUpgradeDialog();
      } else toast.error(err.response?.data?.detail || "Failed to start deletion");
    }
  };

  const progressValue = deletionJob ? ((deletionJob.deleted_count + deletionJob.failed_count) / deletionJob.total_items) * 100 : 0;

  const DeletionProgressBar = () => {
    if (!isDeleting && !deletionJob) return null;
    const failedLogs = deletionJob?.logs?.filter(l => l.startsWith("[FAILED]") || l.startsWith("[ERROR]")) || [];
    return (
      <div className="deletion-progress-bar sticky-progress">
        <div className="progress-header">
          <div className="progress-title">
            {isDeleting ? <><Loader2 className="w-4 h-4 animate-spin" /> Deleting...</> : <><CheckCircle2 className="w-4 h-4 text-green-500" /> Complete</>}
          </div>
          <div className="progress-counts">
            <span className="text-green-500">{deletionJob?.deleted_count || 0}</span>
            {(deletionJob?.failed_count || 0) > 0 && <> / <span style={{ color: "var(--wm-danger)" }}>{deletionJob.failed_count} failed</span></>}
            {" "}/ {deletionJob?.total_items || 0}
          </div>
        </div>
        <Progress value={progressValue} className="progress-bar-main" />
        {deletionJob?.current_sku && isDeleting && (<div className="current-item">Removing: <span className="font-mono">{deletionJob.current_sku}</span></div>)}
        {!isDeleting && failedLogs.length > 0 && (
          <div style={{ marginTop: 8, maxHeight: 120, overflowY: "auto", fontSize: "0.75rem", fontFamily: "monospace", color: "var(--wm-danger)", background: "#FEF2F2", padding: "8px 12px", borderRadius: 6, border: "1px solid #FECACA" }}>
            {failedLogs.slice(0, 20).map((log, i) => (<div key={i}>{log}</div>))}
            {failedLogs.length > 20 && <div>...and {failedLogs.length - 20} more errors</div>}
          </div>
        )}
      </div>
    );
  };

  // Google auth loading
  if (googleLoading) {
    return (<div className="app-container"><Toaster position="top-right" richColors />
      <div className="auth-screen"><div className="auth-box" style={{ textAlign: "center" }}>
        <Loader2 className="w-8 h-8 animate-spin text-blue-500 mx-auto mb-4" /><p>Signing in with Google...</p>
      </div></div></div>);
  }

  // Admin Panel
  if (showAdmin && user?.is_admin) {
    return (<div className="app-container"><Toaster position="top-right" richColors />
      <AdminPanel token={localStorage.getItem("token")} onBack={() => setShowAdmin(false)} /></div>);
  }

  // Status filter dropdown component
  const StatusFilterSelect = ({ value, onChange, testId }) => (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="amount-select" data-testid={testId}><SelectValue placeholder="Filter status" /></SelectTrigger>
      <SelectContent>
        <SelectItem value="all">All Statuses</SelectItem>
        <SelectItem value="PUBLISHED">Published</SelectItem>
        <SelectItem value="UNPUBLISHED">Unpublished</SelectItem>
        <SelectItem value="SYSTEM_PROBLEM">System Problem</SelectItem>
        <SelectItem value="ACTIVE">Active</SelectItem>
        <SelectItem value="RETIRED">Retired</SelectItem>
      </SelectContent>
    </Select>
  );

  return (
    <PayPalScriptProvider options={{ clientId: PAYPAL_CLIENT_ID, currency: "USD" }}>
      <div className="app-container">
        <Toaster position="top-right" richColors />

        <div className="top-banner">
          <AlertTriangle className="w-4 h-4" />
          <span>Walmart takes 24-48 hours to fully remove retired items from Seller Center.</span>
        </div>

        <header className="main-header">
            <div className="header-logo-centered">
              <img src="/logo.png" alt="MarketMop.com" className="header-logo-img" />
              <div className="logo-text">
                <h1>MarketMop.com</h1>
                <p>Walmart Marketplace Bulk Retirement & CSV Exporter Tool</p>
              </div>
            </div>
            <div className="header-actions">
              {isGuest ? (
                <>
                  <span className="trial-badge" data-testid="trial-badge">{trialUsed ? "Trial Used" : `FREE TRIAL (${FREE_TIER_LIMIT} ITEMS)`}</span>
                  <Button data-testid="signin-header-btn" className="header-btn btn-primary" onClick={() => setShowAuthPrompt(true)}>Sign In</Button>
                </>
              ) : (
                <>
                  <div>
                    {user.plan === "lifetime" ? <span className="plan-badge lifetime">Lifetime</span>
                      : user.plan === "monthly" ? <span className="plan-badge monthly">Pro</span>
                      : <span className="plan-badge free">Free ({user.has_used_free_tier ? "Used" : "5 items"})</span>}
                  </div>
                  {user.is_admin && <Button data-testid="admin-btn" variant="ghost" size="sm" onClick={() => setShowAdmin(true)} title="Admin"><Shield className="w-4 h-4" style={{color: "#F08C00"}} /></Button>}
                  <Link to="/listings" data-testid="listings-link" className="header-btn-link" title="Manage Listings"><Edit3 className="w-4 h-4" /></Link>
                  <Button data-testid="help-btn" variant="ghost" size="sm" onClick={() => setShowHelp(true)} title="Help"><HelpCircle className="w-4 h-4" /></Button>
                  <Button data-testid="settings-btn" variant="ghost" size="sm" onClick={() => setShowSettings(true)} title="Settings"><Settings className="w-4 h-4" /></Button>
                  <Button data-testid="upgrade-btn" variant="ghost" size="sm" onClick={openUpgradeDialog} title="Upgrade"><CreditCard className="w-4 h-4" /></Button>
                  <Button data-testid="logout-btn" variant="ghost" size="sm" onClick={handleLogout} title="Logout"><LogOut className="w-4 h-4" /></Button>
                </>
              )}
            </div>
        </header>

        {/* Tab Navigation */}
        <div className="tabs-container" data-testid="tab-nav">
          <button data-testid="tab-retirement" className={`tab-btn ${activeTab === "retirement" ? "active" : ""}`} onClick={() => setActiveTab("retirement")}>
            <Trash2 className="w-4 h-4" /> Bulk Retirement Tool
          </button>
          <button data-testid="tab-exporter" className={`tab-btn ${activeTab === "exporter" ? "active" : ""}`} onClick={() => setActiveTab("exporter")}>
            <FileSpreadsheet className="w-4 h-4" /> CSV Exporter
          </button>
        </div>

        {/* Guest: Credential Entry */}
        {isGuest && !trialValidated && (
          <div className="main-content">
            <Card className="credentials-card" data-testid="trial-creds-card">
              <CardHeader>
                <CardTitle className="card-title"><Rocket className="w-5 h-5" style={{color: "var(--wm-blue)"}} /> Get Started — Enter Your Walmart API Credentials</CardTitle>
                <CardDescription style={{color: "var(--wm-text-secondary)"}}>
                  Try it free! Up to {FREE_TIER_LIMIT} items — no account required.
                  <button className="cred-help" onClick={() => setShowHelp(true)} style={{marginLeft: 4, border: "none", background: "none", cursor: "pointer"}}> How do I get my credentials?</button>
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="credential-fields">
                  <Input data-testid="trial-client-id" placeholder="Walmart Client ID" value={trialCreds.client_id} onChange={(e) => setTrialCreds({ ...trialCreds, client_id: e.target.value })} />
                  <Input data-testid="trial-client-secret" placeholder="Walmart Client Secret" type="password" value={trialCreds.client_secret} onChange={(e) => setTrialCreds({ ...trialCreds, client_secret: e.target.value })} />
                  <Button data-testid="trial-validate-btn" onClick={validateTrialCredentials} disabled={validatingCreds} className="action-btn primary-btn">
                    {validatingCreds ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Validating...</> : "Connect & Start"}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Conditional Tab Content */}
        {(hasCredentials || !isGuest) && (
          <main className="main-content">
            <DeletionProgressBar />

            {/* Guest: Trial used banner */}
            {isGuest && trialUsed && trialValidated && (
              <div style={{ background: "var(--wm-yellow-light)", border: "1px solid var(--wm-yellow)", borderRadius: "var(--wm-radius)", padding: "12px 20px", margin: "16px 0", display: "flex", alignItems: "center", gap: 10 }} data-testid="trial-used-banner">
                <CheckCircle2 className="w-5 h-5" style={{color: "var(--wm-success)"}} />
                <span style={{flex: 1, color: "var(--wm-text)"}}>Your free trial is complete! Create an account and upgrade for unlimited access.</span>
                <Button className="action-btn primary-btn" size="sm" onClick={() => setShowAuthPrompt(true)}>Create Account</Button>
              </div>
            )}

            {/* ===== RETIREMENT TAB ===== */}
            {activeTab === "retirement" && (
              <div data-testid="retirement-tab-content">
                <div className="stats-grid">
                  <Card className="stat-card"><CardHeader className="stat-header"><CardTitle className="stat-title">Selected</CardTitle></CardHeader>
                    <CardContent><div className="stat-value font-mono">{selectedSkus.size}</div></CardContent></Card>
                  <Card className="stat-card"><CardHeader className="stat-header"><CardTitle className="stat-title">Loaded</CardTitle></CardHeader>
                    <CardContent><div className="stat-value font-mono">{inventory.length}</div></CardContent></Card>
                  <Card className="stat-card stat-card-highlight"><CardHeader className="stat-header"><CardTitle className="stat-title">Total Retired</CardTitle></CardHeader>
                    <CardContent><div className="stat-value font-mono text-green-500">{isGuest ? (deletionJob?.deleted_count || 0) : deletionStats.total_unique_retired}</div>
                      {!isGuest && <div className="stat-subtitle">Your all-time deletions</div>}</CardContent></Card>
                </div>

                {hasCredentials && (
                  <div className="action-bar">
                    <Select value={loadAmount.toString()} onValueChange={(v) => setLoadAmount(parseInt(v))}>
                      <SelectTrigger className="amount-select" data-testid="load-amount-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="5">5 items</SelectItem>
                        <SelectItem value="10">10 items</SelectItem>
                        <SelectItem value="20">20 items</SelectItem>
                        <SelectItem value="50">50 items</SelectItem>
                        <SelectItem value="75">75 items</SelectItem>
                        <SelectItem value="100">100 items</SelectItem>
                        <SelectItem value="500">500 items</SelectItem>
                        <SelectItem value="1000">1,000 items</SelectItem>
                        <SelectItem value="3000">3,000 items</SelectItem>
                        <SelectItem value="6000">6,000 items</SelectItem>
                      </SelectContent>
                    </Select>
                    <StatusFilterSelect value={statusFilter} onChange={setStatusFilter} testId="status-filter-select" />
                    <Button data-testid="load-inventory-btn" onClick={loadInventory} disabled={loading || isDeleting} className="action-btn primary-btn">
                      {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Loading...</> : <><RefreshCw className="w-4 h-4 mr-2" />Load Inventory</>}
                    </Button>
                    <Button data-testid="delete-selected-btn" onClick={() => {
                      if (isGuest && trialUsed) { setShowAuthPrompt(true); return; }
                      setShowDeleteDialog(true);
                    }} disabled={selectedSkus.size === 0 || isDeleting} className="action-btn delete-btn">
                      <Trash2 className="w-4 h-4 mr-2" />Delete Selected ({selectedSkus.size})
                    </Button>
                  </div>
                )}

                <Card className="inventory-card" data-testid="inventory-card">
                  <CardHeader>
                    <CardTitle className="card-title">Inventory Items</CardTitle>
                    <CardDescription>
                      {statusFilter !== "all" && `Showing ${filteredInventory.length} of ${inventory.length} items. `}
                      {hasCredentials ? "Select items to retire." : "Enter your Walmart credentials to get started."}
                      {isGuest && !trialUsed && hasCredentials && ` Free trial: ${FREE_TIER_LIMIT} items.`}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {inventory.length === 0 ? (
                      <div className="empty-state" data-testid="empty-state">
                        <img src="/logo.png" alt="Market Mop" className="empty-logo" />
                        <p>{hasCredentials ? "Click 'Load Inventory' to fetch your items." : (isGuest ? "Enter your credentials above to begin." : "Add your Walmart credentials in Settings to get started.")}</p>
                        {!isGuest && !user?.has_walmart_credentials && <Button data-testid="add-credentials-btn" onClick={() => setShowSettings(true)} className="mt-4">Add Credentials</Button>}
                      </div>
                    ) : (
                      <div className="table-container" data-testid="retirement-table" style={{ maxHeight: "600px", overflowY: "auto", contain: "layout" }}>
                        <table className="data-table">
                          <thead>
                            <tr>
                              <th className="w-12"><Checkbox data-testid="select-all-checkbox" checked={selectedSkus.size === filteredInventory.length && filteredInventory.length > 0} onCheckedChange={toggleSelectAll} /></th>
                              <th className="w-36">SKU</th>
                              <th>Product Name</th>
                              <th className="w-32 text-center">Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {filteredInventory.map((item, idx) => {
                              const isLocked = isFreeTier && idx >= freeItemLimit;
                              return (
                                <tr key={item.sku} className={`${selectedSkus.has(item.sku) ? "selected-row" : ""} ${isLocked ? "locked-row" : ""}`} data-testid={`retirement-row-${item.sku}`}>
                                  <td><Checkbox data-testid={`select-item-${item.sku}`} checked={selectedSkus.has(item.sku)} onCheckedChange={() => toggleSelect(item.sku)} disabled={isLocked} /></td>
                                  <td className="font-mono text-sm">{item.sku}</td>
                                  <td className="truncate max-w-xs">{item.product_name}</td>
                                  <td className="text-center"><span className={`status-badge status-${item.lifecycle_status?.toLowerCase()}`}>{item.lifecycle_status}</span></td>
                                  {isLocked && idx === freeItemLimit && (
                                    <td colSpan={0} className="locked-overlay-anchor" />
                                  )}
                                </tr>
                              );
                            })}
                            {isFreeTier && filteredInventory.length > freeItemLimit && (
                              <tr>
                                <td colSpan={4}>
                                  <div className="upgrade-banner" data-testid="upgrade-banner">
                                    <CreditCard className="w-5 h-5" />
                                    <span>{filteredInventory.length - freeItemLimit} more items locked. <button className="upgrade-banner-btn" onClick={isGuest ? () => setShowAuthPrompt(true) : openUpgradeDialog}>{isGuest ? "Sign up" : "Upgrade"} to unlock all items.</button></span>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </CardContent>
                </Card>

                <div className="stats-grid bottom-stats">
                  <Card className="stat-card"><CardHeader className="stat-header"><CardTitle className="stat-title">Selected</CardTitle></CardHeader>
                    <CardContent><div className="stat-value font-mono">{selectedSkus.size}</div></CardContent></Card>
                  <Card className="stat-card stat-card-highlight"><CardHeader className="stat-header"><CardTitle className="stat-title">Total Retired</CardTitle></CardHeader>
                    <CardContent><div className="stat-value font-mono text-green-500">{isGuest ? (deletionJob?.deleted_count || 0) : deletionStats.total_unique_retired}</div></CardContent></Card>
                </div>
                <DeletionProgressBar />
              </div>
            )}

            {/* ===== CSV EXPORTER TAB ===== */}
            {activeTab === "exporter" && (
              <div data-testid="exporter-tab-content">
                <Card className="exporter-intro-card">
                  <CardHeader>
                    <CardTitle className="card-title"><FileSpreadsheet className="w-5 h-5 text-blue-400" /> Inventory CSV Exporter</CardTitle>
                    <CardDescription>
                      Load your Walmart inventory and export it as a CSV file with all product details.
                      {isGuest && !trialUsed && ` Free trial: export up to ${FREE_CSV_LIMIT} items.`}
                      {isGuest && trialUsed && " Create an account and upgrade for unlimited exports."}
                      {!isGuest && user?.plan === "free" && " Upgrade for unlimited CSV exports."}
                    </CardDescription>
                  </CardHeader>
                </Card>

                {hasCredentials && (
                  <div className="action-bar">
                    <Select value={csvLoadAmount.toString()} onValueChange={(v) => setCsvLoadAmount(parseInt(v))}>
                      <SelectTrigger className="amount-select" data-testid="csv-load-amount-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="5">5 items</SelectItem>
                        <SelectItem value="10">10 items</SelectItem>
                        <SelectItem value="20">20 items</SelectItem>
                        <SelectItem value="50">50 items</SelectItem>
                        <SelectItem value="75">75 items</SelectItem>
                        <SelectItem value="100">100 items</SelectItem>
                        <SelectItem value="500">500 items</SelectItem>
                        <SelectItem value="1000">1,000 items</SelectItem>
                        <SelectItem value="3000">3,000 items</SelectItem>
                        <SelectItem value="6000">6,000 items</SelectItem>
                        <SelectItem value="10000">10,000 items</SelectItem>
                      </SelectContent>
                    </Select>
                    <StatusFilterSelect value={csvStatusFilter} onChange={setCsvStatusFilter} testId="csv-status-filter-select" />
                    <Button data-testid="csv-load-btn" onClick={loadCsvInventory} disabled={csvLoading} className="action-btn primary-btn">
                      {csvLoading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Loading...</> : <><RefreshCw className="w-4 h-4 mr-2" />Load Inventory</>}
                    </Button>
                    <Button data-testid="csv-export-btn" onClick={() => exportCSV(filteredCsvInventory)} disabled={csvInventory.length === 0} className="action-btn csv-export-btn">
                      <Download className="w-4 h-4 mr-2" />Export CSV ({filteredCsvInventory.length} items)
                    </Button>
                  </div>
                )}

                {/* CSV Stats */}
                <div className="stats-grid" style={{ gridTemplateColumns: "repeat(2, 1fr)" }}>
                  <Card className="stat-card"><CardHeader className="stat-header"><CardTitle className="stat-title">Total Loaded</CardTitle></CardHeader>
                    <CardContent><div className="stat-value font-mono">{csvInventory.length}</div></CardContent></Card>
                  <Card className="stat-card"><CardHeader className="stat-header"><CardTitle className="stat-title">Filtered</CardTitle></CardHeader>
                    <CardContent><div className="stat-value font-mono">{filteredCsvInventory.length}</div></CardContent></Card>
                </div>

                {/* CSV Inventory Preview */}
                <Card className="inventory-card" data-testid="csv-inventory-card">
                  <CardHeader>
                    <CardTitle className="card-title">Inventory Preview</CardTitle>
                    <CardDescription>
                      {csvStatusFilter !== "all" && `Showing ${filteredCsvInventory.length} of ${csvInventory.length} items. `}
                      {csvInventory.length > 0 ? "Preview your data, then click Export CSV to download." : (hasCredentials ? "Click 'Load Inventory' to fetch items for export." : "Enter your Walmart credentials to begin.")}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {csvInventory.length === 0 ? (
                      <div className="empty-state" data-testid="csv-empty-state">
                        <FileSpreadsheet className="empty-icon" style={{ color: "#3b82f6" }} />
                        <p>{hasCredentials ? "Load your inventory to preview and export as CSV." : "Connect your Walmart credentials to get started."}</p>
                      </div>
                    ) : (
                      <div className="table-container" data-testid="csv-table" style={{ maxHeight: "600px", overflowY: "auto", contain: "layout" }}>
                        <table className="data-table">
                          <thead>
                            <tr>
                              <th className="w-28">SKU</th>
                              <th>Product Name</th>
                              <th>Product ID</th>
                              <th>UPC</th>
                              <th>Type</th>
                              <th className="text-right">Price</th>
                              <th className="text-right">Qty</th>
                              <th className="text-center">Lifecycle</th>
                              <th className="text-center">Published</th>
                            </tr>
                          </thead>
                          <tbody>
                            {filteredCsvInventory.map((item, idx) => {
                              const isLocked = isFreeTier && idx >= freeItemLimit;
                              return (
                                <tr key={item.sku} className={isLocked ? "locked-row" : ""} data-testid={`csv-row-${item.sku}`}>
                                  <td className="font-mono text-sm">{item.sku}</td>
                                  <td className="truncate max-w-xs">{item.product_name}</td>
                                  <td className="font-mono text-xs">{item.product_id || "-"}</td>
                                  <td className="font-mono text-xs">{item.upc || "-"}</td>
                                  <td className="text-xs">{item.product_type || "-"}</td>
                                  <td className="text-right font-mono">{item.price != null ? `$${Number(item.price).toFixed(2)}` : "-"}</td>
                                  <td className="text-right font-mono">{item.quantity ?? "-"}</td>
                                  <td className="text-center"><span className={`status-badge status-${item.lifecycle_status?.toLowerCase()}`}>{item.lifecycle_status}</span></td>
                                  <td className="text-center"><span className={`status-badge status-${item.published_status?.toLowerCase()}`}>{item.published_status}</span></td>
                                </tr>
                              );
                            })}
                            {isFreeTier && filteredCsvInventory.length > freeItemLimit && (
                              <tr>
                                <td colSpan={9}>
                                  <div className="upgrade-banner" data-testid="csv-upgrade-banner">
                                    <CreditCard className="w-5 h-5" />
                                    <span>{filteredCsvInventory.length - freeItemLimit} more items locked. <button className="upgrade-banner-btn" onClick={isGuest ? () => setShowAuthPrompt(true) : openUpgradeDialog}>{isGuest ? "Sign up" : "Upgrade"} to export all items.</button></span>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                        {filteredCsvInventory.length > 100 && (
                          <div className="text-center text-sm py-3" style={{color: "#6b7280"}}>Showing all {filteredCsvInventory.length} items. All items will be included in the CSV export.</div>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            )}
          </main>
        )}

        {/* Footer */}
        <footer className="app-footer" data-testid="app-footer">
          <div className="footer-main">
            <div className="footer-payment-section">
              <span className="footer-payment-label">Accepted Payments</span>
              <div className="footer-payment-icons">
                <svg className="payment-icon" viewBox="0 0 38 24" xmlns="http://www.w3.org/2000/svg"><rect x="0.5" y="0.5" width="37" height="23" rx="3" fill="#fff" stroke="#d9d9d9"/><path d="M15.764 16.23h-2.39l1.496-9.218h2.39l-1.496 9.218z" fill="#00579F"/><path d="M24.752 7.24c-.474-.187-.962-.287-1.456-.29-1.605 0-2.736.858-2.745 2.087-.009.908.81 1.414 1.428 1.717.633.31.846.508.843.785-.004.424-.505.618-.972.618-.65 0-.997-.096-1.531-.332l-.21-.1-.228 1.413c.38.176 1.083.329 1.813.337 1.706 0 2.816-.844 2.828-2.16.006-.72-.428-1.268-1.37-1.72-.57-.293-.92-.488-.916-.785 0-.263.296-.545.936-.545.427-.01.848.07 1.236.236l.148.073.224-1.334z" fill="#00579F"/><path d="M28.432 7.012h-1.254c-.388 0-.679.112-.85.522l-2.414 5.696h1.706l.34-.942h2.084l.198.942h1.506l-1.316-6.218zm-2.008 4.008c.135-.363.65-1.763.65-1.763-.01.016.134-.365.216-.601l.11.543.378 1.821h-1.354z" fill="#00579F"/><path d="M12.296 7.012l-1.593 6.29-.17-.86c-.296-.963-1.222-2.008-2.256-2.53l1.455 5.516h1.72l2.558-8.416h-1.714z" fill="#00579F"/><path d="M9.42 7.012H6.833l-.023.143c2.04.522 3.39 1.783 3.948 3.297l-.57-2.89c-.098-.397-.383-.524-.768-.55z" fill="#FAA61A"/></svg>
                <svg className="payment-icon" viewBox="0 0 38 24" xmlns="http://www.w3.org/2000/svg"><rect x="0.5" y="0.5" width="37" height="23" rx="3" fill="#fff" stroke="#d9d9d9"/><circle cx="15" cy="12" r="7" fill="#EB001B"/><circle cx="23" cy="12" r="7" fill="#F79E1B"/><path d="M19 7.35c1.58 1.2 2.6 3.08 2.6 5.2 0 2.12-1.02 4-2.6 5.2-1.58-1.2-2.6-3.08-2.6-5.2 0-2.12 1.02-4 2.6-5.2z" fill="#FF5F00"/></svg>
                <svg className="payment-icon" viewBox="0 0 38 24" xmlns="http://www.w3.org/2000/svg"><rect x="0.5" y="0.5" width="37" height="23" rx="3" fill="#fff" stroke="#d9d9d9"/><path d="M8.971 10.268l.774-1.876a.367.367 0 0 0-.192-.484l-.324-.14a.36.36 0 0 0-.137-.028H7.014a.36.36 0 0 0-.339.24l-2.39 6.53a.36.36 0 0 0 .339.48h1.158a.36.36 0 0 0 .339-.24l.6-1.64h1.468l.6 1.64a.36.36 0 0 0 .339.24h1.158a.36.36 0 0 0 .339-.48L8.971 10.268zm-1.53 1.986l.613-1.68.614 1.68H7.441z" fill="#2557D6"/><text x="14.5" y="16" fontFamily="Arial" fontSize="8" fontWeight="bold" fill="#2557D6">AMEX</text></svg>
                <svg className="payment-icon paypal-icon" viewBox="0 0 38 24" xmlns="http://www.w3.org/2000/svg"><rect x="0.5" y="0.5" width="37" height="23" rx="3" fill="#fff" stroke="#d9d9d9"/><text x="8" y="16" fontFamily="Arial" fontSize="8" fontWeight="bold" fill="#003087">Pay</text><text x="20" y="16" fontFamily="Arial" fontSize="8" fontWeight="bold" fill="#009CDE">Pal</text></svg>
              </div>
            </div>
            <button data-testid="help-ticket-btn" className="footer-ticket-btn" onClick={() => setShowTicketDialog(true)}>
              <LifeBuoy className="w-4 h-4" /> Help / Submit Ticket
            </button>
            <button data-testid="aaa-admin-link" className="aaa-link" onClick={() => setShowAuthPrompt(true)}>AAA</button>
          </div>
        </footer>

        {/* Delete Confirmation Dialog */}        <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
          <DialogContent className="delete-dialog">
            <DialogHeader>
              <DialogTitle className="dialog-title"><AlertTriangle className="w-6 h-6 text-red-500" /> Confirm Deletion</DialogTitle>
              <DialogDescription>Delete {selectedSkus.size} items? This cannot be undone.</DialogDescription>
            </DialogHeader>
            <div className="walmart-notice"><AlertTriangle className="w-4 h-4" /><span>Items take 24-48 hours to fully disappear from Walmart.</span></div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>Cancel</Button>
              <Button data-testid="confirm-delete-btn" className="destructive-btn" onClick={startDeletion}><Trash2 className="w-4 h-4 mr-2" />Delete {selectedSkus.size} Items</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Auth/Signup Prompt */}
        <Dialog open={showAuthPrompt} onOpenChange={setShowAuthPrompt}>
          <DialogContent className="auth-prompt-dialog">
            <DialogHeader>
              <div className="auth-prompt-brand">
                <img src="/logo.png" alt="Market Mop" className="auth-prompt-logo" />
              </div>
              <DialogTitle className="dialog-title">
                {trialUsed ? <><CheckCircle2 className="w-6 h-6 text-green-500" /> Create an Account to Continue</> : "Sign In to Your Account"}
              </DialogTitle>
              <DialogDescription>{trialUsed ? "Your free trial was a success! Sign up to unlock unlimited inventory management." : "Sign in or create an account to access all features."}</DialogDescription>
            </DialogHeader>
            <div className="auth-prompt-content">
              <button data-testid="google-signin-btn" className="google-btn" onClick={handleGoogleLogin}>
                <svg width="18" height="18" viewBox="0 0 18 18"><path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z" fill="#4285F4"/><path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z" fill="#34A853"/><path d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05"/><path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 6.29C4.672 4.163 6.656 2.58 9 3.58z" fill="#EA4335"/></svg>
                Continue with Google
              </button>
              <div className="auth-divider"><span>or</span></div>
              <form onSubmit={handleAuth} className="auth-form" data-testid="auth-prompt-form">
                <Input data-testid="auth-email-input" type="text" placeholder="Username or Email" value={authEmail} onChange={(e) => setAuthEmail(e.target.value)} required />
                <Input data-testid="auth-password-input" type="password" placeholder="Password" value={authPassword} onChange={(e) => setAuthPassword(e.target.value)} required />
                <Button data-testid="auth-submit-btn" type="submit" className="auth-btn" disabled={authLoading}>
                  {authLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : (authMode === "login" ? "Sign In" : "Create Account")}
                </Button>
              </form>
              <div className="auth-switch">
                {authMode === "login" ? <p>Don't have an account? <button data-testid="switch-to-register" onClick={() => setAuthMode("register")}>Sign Up</button></p>
                  : <p>Already have an account? <button data-testid="switch-to-login" onClick={() => setAuthMode("login")}>Sign In</button></p>}
              </div>
              {trialUsed && <div className="pricing-teaser"><p><strong>$50/month</strong> or <strong>$150 lifetime</strong> for unlimited deletions</p></div>}
            </div>
          </DialogContent>
        </Dialog>

        {/* Settings Dialog */}
        <Dialog open={showSettings} onOpenChange={setShowSettings}>
          <DialogContent className="settings-dialog">
            <DialogHeader><DialogTitle>Walmart API Credentials</DialogTitle><DialogDescription>Enter your Walmart Marketplace API credentials to connect your account.</DialogDescription></DialogHeader>
            <div className="settings-form">
              <Input data-testid="walmart-client-id-input" placeholder="Client ID" value={walmartClientId} onChange={(e) => setWalmartClientId(e.target.value)} />
              <Input data-testid="walmart-secret-input" placeholder="Client Secret" type="password" value={walmartSecret} onChange={(e) => setWalmartSecret(e.target.value)} />
              <div className="help-link"><HelpCircle className="w-4 h-4" /><span>Don't have credentials? <button onClick={() => { setShowSettings(false); setShowHelp(true); }}>See how to get them</button></span></div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowSettings(false)}>Cancel</Button>
              <Button data-testid="save-credentials-btn" onClick={saveWalmartCredentials} disabled={savingCreds}>{savingCreds ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save Credentials"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Help Dialog */}
        <Dialog open={showHelp} onOpenChange={setShowHelp}>
          <DialogContent className="help-dialog">
            <DialogHeader><DialogTitle>How to Get Walmart API Credentials</DialogTitle></DialogHeader>
            <div className="help-content">
              <ol>
                <li><strong>1. Go to Walmart Developer Portal</strong><br/><a href="https://developer.walmart.com/login" target="_blank" rel="noopener noreferrer" className="help-link-btn"><ExternalLink className="w-4 h-4" /> developer.walmart.com/login</a></li>
                <li><strong>2. Sign in</strong> with your Walmart Seller Center account</li>
                <li><strong>3. Go to "My API Keys"</strong> in the dashboard</li>
                <li><strong>4. Create a new API key</strong> if you don't have one</li>
                <li><strong>5. Copy your Client ID and Client Secret</strong></li>
                <li><strong>6. Paste them</strong> {isGuest ? "in the form above" : "in the Settings dialog"}</li>
              </ol>
              <div className="help-note"><AlertTriangle className="w-4 h-4" /><span>Keep your credentials secure. Never share them with anyone.</span></div>
            </div>
            <DialogFooter>
              {isGuest ? <Button onClick={() => setShowHelp(false)}>Got it</Button> : <Button onClick={() => { setShowHelp(false); setShowSettings(true); }}>Add My Credentials</Button>}
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Upgrade Dialog */}
        <Dialog open={showUpgrade} onOpenChange={setShowUpgrade}>
          <DialogContent style={{ maxWidth: 700 }}>
            <DialogHeader>
              <DialogTitle style={{ fontSize: "1.25rem" }}>Upgrade Your Plan</DialogTitle>
              <DialogDescription style={{ color: "var(--wm-text-secondary)" }}>Unlock unlimited access with a paid plan.</DialogDescription>
            </DialogHeader>
            {pricingPlans.length > 0 ? (
              <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(120px, 1fr))", gap: 8 }}>
                  {pricingPlans.map(plan => (
                    <button
                      key={plan.plan_id}
                      data-testid={`upgrade-plan-${plan.plan_id}`}
                      onClick={() => setSelectedPlan(plan.plan_id)}
                      style={{
                        padding: "14px 12px",
                        borderRadius: "var(--wm-radius)",
                        border: selectedPlan === plan.plan_id ? "2px solid var(--wm-blue)" : "1px solid var(--wm-border)",
                        background: selectedPlan === plan.plan_id ? "var(--wm-blue-light)" : "var(--wm-white)",
                        cursor: "pointer",
                        textAlign: "center",
                        transition: "all 0.15s",
                      }}
                    >
                      <div style={{ fontSize: "0.6875rem", fontWeight: 600, textTransform: "uppercase", color: "var(--wm-text-muted)", marginBottom: 4 }}>{plan.name}</div>
                      <div style={{ fontSize: "1.5rem", fontWeight: 700, color: selectedPlan === plan.plan_id ? "var(--wm-blue)" : "var(--wm-text)" }}>${plan.price.toFixed(0)}</div>
                      <div style={{ fontSize: "0.6875rem", color: "var(--wm-text-muted)" }}>{plan.duration_days > 0 ? `${plan.duration_days} days` : "Forever"}</div>
                    </button>
                  ))}
                </div>

                {selectedPlan && (() => {
                  const plan = pricingPlans.find(p => p.plan_id === selectedPlan);
                  if (!plan) return null;
                  return (
                    <div style={{ borderTop: "1px solid var(--wm-border)", paddingTop: 16 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                        <div>
                          <div style={{ fontWeight: 700, fontSize: "1rem", color: "var(--wm-text)" }}>{plan.name} Plan</div>
                          <div style={{ fontSize: "0.8125rem", color: "var(--wm-text-secondary)" }}>{plan.description}</div>
                        </div>
                        <div style={{ fontSize: "1.5rem", fontWeight: 700, color: "var(--wm-blue)" }}>${plan.price.toFixed(2)}</div>
                      </div>
                      <PayPalButtons style={{ layout: "vertical", shape: "rect" }}
                        createOrder={(data, actions) => actions.order.create({ purchase_units: [{ amount: { value: plan.price.toFixed(2) }, description: `Market Mop - ${plan.name} Access` }] })}
                        onApprove={async (data, actions) => {
                          const details = await actions.order.capture();
                          try { await axios.post(`${API}/payments/verify`, { order_id: details.id, plan: plan.plan_id }, { headers: getAuthHeader() }); toast.success("Payment successful! Your plan has been upgraded."); fetchUser(localStorage.getItem("token")); setShowUpgrade(false); }
                          catch (err) { toast.error(err.response?.data?.detail || "Payment verification failed"); }
                        }} />
                    </div>
                  );
                })()}
              </div>
            ) : (
              <div style={{ textAlign: "center", padding: 24, color: "var(--wm-text-muted)" }}>Loading plans...</div>
            )}
          </DialogContent>
        </Dialog>

        {/* Help Ticket Dialog */}
        <Dialog open={showTicketDialog} onOpenChange={setShowTicketDialog}>
          <DialogContent style={{ maxWidth: 500 }}>
            <DialogHeader>
              <DialogTitle style={{ display: "flex", alignItems: "center", gap: 8 }}><LifeBuoy className="w-5 h-5" style={{ color: "var(--wm-blue)" }} /> Submit a Help Ticket</DialogTitle>
              <DialogDescription>Describe your issue and we'll get back to you as soon as possible.</DialogDescription>
            </DialogHeader>
            <div style={{ display: "flex", flexDirection: "column", gap: 14, padding: "4px 0" }}>
              {isGuest && (
                <div>
                  <label style={{ display: "block", fontSize: "0.8125rem", fontWeight: 600, color: "var(--wm-text-secondary)", marginBottom: 4 }}>Your Email</label>
                  <Input data-testid="ticket-email" value={ticketEmail} onChange={e => setTicketEmail(e.target.value)} placeholder="your@email.com" />
                </div>
              )}
              <div>
                <label style={{ display: "block", fontSize: "0.8125rem", fontWeight: 600, color: "var(--wm-text-secondary)", marginBottom: 4 }}>Subject *</label>
                <Input data-testid="ticket-subject" value={ticketSubject} onChange={e => setTicketSubject(e.target.value)} placeholder="Brief summary of your issue" />
              </div>
              <div>
                <label style={{ display: "block", fontSize: "0.8125rem", fontWeight: 600, color: "var(--wm-text-secondary)", marginBottom: 4 }}>Description *</label>
                <textarea data-testid="ticket-message" value={ticketMessage} onChange={e => setTicketMessage(e.target.value)} placeholder="Please describe your issue in detail..." rows={5} style={{ width: "100%", padding: "8px 12px", border: "1px solid var(--wm-border)", borderRadius: "var(--wm-radius)", fontSize: "0.875rem", fontFamily: "inherit", resize: "vertical" }} />
              </div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setShowTicketDialog(false)}>Cancel</Button>
              <Button data-testid="ticket-submit-btn" onClick={submitTicket} disabled={submittingTicket} className="action-btn primary-btn" style={{ gap: 6 }}>
                {submittingTicket ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                {submittingTicket ? "Submitting..." : "Submit Ticket"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </PayPalScriptProvider>
  );
}

export default App;
